/* eslint-disable */
import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import { MESSAGE_TYPES } from '../constants';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import livekitService from '../services/livekitService';

// Tab types
const TAB_TYPES = {
  GENERAL: 'general',
  USER_SPEECH: 'user_speech',
  AI_SPEECH: 'ai_speech',
  AI_INTENT: 'ai_intent',
  SYSTEM: 'system',
  CALL_INTERFACE: 'call_interface'
};

// Initial state
const initialState = {
  messages: [],
  currentTab: TAB_TYPES.GENERAL,
  userSpeechBuffer: '',
  aiSpeechBuffer: '',
  isConnected: false,
  connectionStatus: 'disconnected',
  error: null,
  messageCounter: 0 // Add counter for unique message IDs
};

// Action types
const ACTIONS = {
  ADD_MESSAGE: 'ADD_MESSAGE',
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  SET_ERROR: 'SET_ERROR',
  SET_CURRENT_TAB: 'SET_CURRENT_TAB',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  UPDATE_USER_SPEECH_BUFFER: 'UPDATE_USER_SPEECH_BUFFER',
  UPDATE_AI_SPEECH_BUFFER: 'UPDATE_AI_SPEECH_BUFFER',
  FINALIZE_USER_SPEECH: 'FINALIZE_USER_SPEECH',
  FINALIZE_AI_SPEECH: 'FINALIZE_AI_SPEECH'
};

// Reducer
function chatReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
        messageCounter: state.messageCounter + 1
      };
    
    case ACTIONS.SET_CONNECTION_STATUS:
      return {
        ...state,
        isConnected: action.payload === 'connected',
        connectionStatus: action.payload
      };
    
    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };
    
    case ACTIONS.SET_CURRENT_TAB:
      return {
        ...state,
        currentTab: action.payload
      };
    
    case ACTIONS.CLEAR_MESSAGES:
      return {
        ...state,
        messages: [],
        userSpeechBuffer: '',
        aiSpeechBuffer: '',
        messageCounter: 0
      };
    
    case ACTIONS.UPDATE_USER_SPEECH_BUFFER:
      return {
        ...state,
        userSpeechBuffer: state.userSpeechBuffer + action.payload
      };
    
    case ACTIONS.UPDATE_AI_SPEECH_BUFFER:
      return {
        ...state,
        aiSpeechBuffer: state.aiSpeechBuffer + action.payload
      };
    
    case ACTIONS.FINALIZE_USER_SPEECH:
      const finalUserText = action.payload || state.userSpeechBuffer;
      if (finalUserText.trim()) {
        const userMessage = {
          id: `user_${state.messageCounter}_${Date.now()}`,
          type: 'user_speech_chunk',
          content: finalUserText,
          timestamp: new Date().toISOString(),
          tab: 'user_speech'
        };
        return { 
          ...state, 
          messages: [...state.messages, userMessage], 
          userSpeechBuffer: '',
          messageCounter: state.messageCounter + 1
        };
      }
      return { ...state, userSpeechBuffer: '' };
    
    case ACTIONS.FINALIZE_AI_SPEECH:
      if (state.aiSpeechBuffer.trim()) {
        const aiMessage = {
          id: `ai_${state.messageCounter}_${Date.now()}`,
          type: MESSAGE_TYPES.AI_SPEECH_CHUNK,
          content: state.aiSpeechBuffer,
          timestamp: new Date().toISOString(),
          tab: TAB_TYPES.AI_SPEECH
        };
        return { 
          ...state, 
          messages: [...state.messages, aiMessage], 
          aiSpeechBuffer: '',
          messageCounter: state.messageCounter + 1
        };
      }
      return state;
    
    default:
      return state;
  }
}

// Create context
const ChatContext = createContext();

// Provider component
export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const dataChannelSetupRef = useRef(false);
  const { sessionId } = useAuth();

  const sendCurrentRuntimeData = async () => {
    if (!sessionId) {
      console.warn('⚠️ Cannot send runtime data, no session ID available.');
      return;
    }
    try {
      console.log(`🎤 User started speaking. Sending runtime data for session: ${sessionId}`);
      await apiService.sendRuntimeData(sessionId);
      console.log('✅ Runtime data sent successfully.');
    } catch (error) {
      console.error('❌ Failed to send runtime data on speech start:', error);
    }
  };

  const setupLiveKitDataChannel = (currentSessionId) => {
    if (dataChannelSetupRef.current) {
      console.log('🔌 LiveKit data channel already set up, skipping setup.');
      return;
    }
    
    try {
      console.log(`🔗 Setting up LiveKit data channel for session: ${currentSessionId}`);
      
      // Set up data received handler
      livekitService.setOnDataReceived((payload, participant) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          console.log('📨 LiveKit: Received data message:', data);
          handleStreamingMessage(data);
        } catch (error) {
          console.error('❌ Error parsing LiveKit data message:', error);
        }
      });
      
      // Set up connection state change handler
      livekitService.setOnConnectionStateChange((state) => {
        console.log(`🔄 LiveKit: Connection state changed: ${state}`);
        if (state === 'connected') {
          dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: 'connected' });
        } else if (state === 'disconnected') {
          dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: 'disconnected' });
        } else if (state === 'reconnecting') {
          dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: 'reconnecting' });
        }
      });
      
      dataChannelSetupRef.current = true;
      console.log('✅ LiveKit data channel setup completed');
      
    } catch (error) {
      console.error('❌ Error setting up LiveKit data channel:', error);
    }
  };

  const cleanupLiveKitDataChannel = () => {
    if (dataChannelSetupRef.current) {
      console.log('🔌 Cleaning up LiveKit data channel handlers.');
      // Clear the data received handler
      livekitService.setOnDataReceived(null);
      livekitService.setOnConnectionStateChange(null);
      dataChannelSetupRef.current = false;
    }
  };

  const handleStreamingMessage = (data) => {
    switch (data.type) {
      case MESSAGE_TYPES.USER_SPEECH_CHUNK:
        if (data.data.is_final) {
          dispatch({ type: ACTIONS.FINALIZE_USER_SPEECH, payload: data.data.text_chunk });
        } else {
          dispatch({ type: ACTIONS.UPDATE_USER_SPEECH_BUFFER, payload: data.data.text_chunk });
        }
        break;
      
      case MESSAGE_TYPES.AI_SPEECH_CHUNK:
        if (data.data.is_final) {
          dispatch({ type: ACTIONS.FINALIZE_AI_SPEECH });
        } else {
          dispatch({ type: ACTIONS.UPDATE_AI_SPEECH_BUFFER, payload: data.data.text_chunk });
        }
        break;
      
      case MESSAGE_TYPES.AI_INTENT:
        const intentMessage = { 
          id: `intent_${state.messageCounter}_${Date.now()}`, 
          type: MESSAGE_TYPES.AI_INTENT, 
          content: data.data, 
          timestamp: new Date().toISOString(), 
          tab: TAB_TYPES.AI_INTENT 
        };
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: intentMessage });
        break;

      case MESSAGE_TYPES.AI_INTERRUPTED:
        sendCurrentRuntimeData();
        const interruptedMessage = { 
          id: `interrupted_${state.messageCounter}_${Date.now()}`, 
          type: MESSAGE_TYPES.AI_INTERRUPTED, 
          content: data.data.message, 
          timestamp: new Date().toISOString(), 
          tab: TAB_TYPES.SYSTEM 
        };
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: interruptedMessage });
        break;
      
      case MESSAGE_TYPES.REQUEST_RUNTIME_DATA:
        sendCurrentRuntimeData();
        break;
      
      case MESSAGE_TYPES.CONNECTION:
        dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: data.status });
        break;
      
      case MESSAGE_TYPES.KEEPALIVE:
        // This is just a heartbeat, no action needed.
        break;
      
      default:
        console.log('📡 Unknown message type:', data.type);
    }
  };

  const setupLiveKit = useCallback(() => {
    if (sessionId) {
      setupLiveKitDataChannel(sessionId);
    }
    return cleanupLiveKitDataChannel;
  }, [sessionId, setupLiveKitDataChannel, cleanupLiveKitDataChannel]);

  useEffect(() => {
    const cleanup = setupLiveKit();
    return () => {
      cleanup?.();
    };
  }, [setupLiveKit]);

  const changeTab = (tab) => {
    // dispatch({ type: ACTIONS.SET_CURRENT_TAB, payload: tab });
  };

  const clearMessages = () => {
    dispatch({ type: ACTIONS.CLEAR_MESSAGES });
  };

  const getMessagesForCurrentTab = () => {
    if (state.currentTab === TAB_TYPES.GENERAL) {
      return state.messages;
    }
    return state.messages.filter(msg => msg.tab === state.currentTab);
  };

  const getTabCounts = () => {
    const counts = {};
    Object.values(TAB_TYPES).forEach(tab => {
      if (tab === TAB_TYPES.GENERAL) {
        counts[tab] = state.messages.length;
      } else {
        counts[tab] = state.messages.filter(msg => msg.tab === tab).length;
      }
    });
    return counts;
  };

  const value = {
    ...state,
    changeTab,
    clearMessages,
    getMessagesForCurrentTab,
    getTabCounts,
    MESSAGE_TYPES,
    TAB_TYPES
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

// Hook to use chat context
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}